import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { 
  Smartphone, 
  Shield, 
  Zap, 
  CloudSun, 
  Palette, 
  Calendar,
  Timer,
  Wifi,
  Star,
  CheckCircle2
} from 'lucide-react';

const PermanentLightingSection = () => {
  const features = [
    {
      icon: <Smartphone className="w-8 h-8 text-blue-400" />,
      title: 'Smart App Control',
      description: 'Control your lights from anywhere with our intuitive smartphone app. Change colors, patterns, and schedules with a tap.'
    },
    {
      icon: <Palette className="w-8 h-8 text-blue-400" />,
      title: 'Unlimited Colors',
      description: '16 million color combinations with RGBW technology. Create any look you can imagine for any occasion.'
    },
    {
      icon: <Calendar className="w-8 h-8 text-blue-400" />,
      title: 'Year-Round Use',
      description: 'Perfect for holidays, events, security lighting, and everyday ambiance. One system, endless possibilities.'
    },
    {
      icon: <CloudSun className="w-8 h-8 text-blue-400" />,
      title: 'Weatherproof Design',
      description: 'Built to withstand extreme temperatures from -40°C to +65°C. Rain, snow, heat - no problem.'
    },
    {
      icon: <Zap className="w-8 h-8 text-blue-400" />,
      title: 'Energy Efficient',
      description: 'LED technology that uses less power while providing class leading brightness for less.'
    },
    {
      icon: <Shield className="w-8 h-8 text-blue-400" />,
      title: '20-Year Warranty',
      description: 'Industry-leading warranty coverage. Your investment is protected for decades.'
    },
    {
      icon: <Timer className="w-8 h-8 text-blue-400" />,
      title: 'Automated Scheduling',
      description: 'Set it and forget it. Schedule different lighting displays for different times and occasions.'
    },
    {
      icon: <Wifi className="w-8 h-8 text-blue-400" />,
      title: 'Versatility Like None Other',
      description: 'The only provider on the market to combine Neon strips, landscape lighting, and holiday lighting for luxury displays to WOW all year round.'
    }
  ];

  const benefits = [
    'Never climb a ladder again - no more dangerous holiday light installation',
    'Save money over time - no annual installation costs',
    'Discreet by day, dazzling by night with sleek aluminum tracks',
    'Professionally installed by certified technicians',
    'Preset library with thousands of pre-programmed displays',
    'Create custom animations and effects',
    'Enhance home security with programmable lighting',
    'Increase property value and curb appeal'
  ];

  const useCases = [
    {
      title: 'Holiday Celebrations',
      description: 'Christmas, Halloween, Easter, Thanksgiving, St. Patrick\'s Day, and more',
      image: 'https://customer-assets.emergentagent.com/job_holiday-lights-pro/artifacts/l01wzac3_1.1.webp'
    },
    {
      title: 'Special Events',
      description: 'Birthdays, graduations, sports games, and celebrations',
      image: 'https://customer-assets.emergentagent.com/job_holiday-lights-pro/artifacts/37quqorv_480157538_664118736133897_2485922308875956844_n.jpg'
    },
    {
      title: 'Everyday Elegance',
      description: 'Architectural accent lighting and security illumination',
      image: 'https://customer-assets.emergentagent.com/job_holiday-lights-pro/artifacts/qefdw8jy_1.2.webp'
    }
  ];

  const scrollToQuote = () => {
    document.getElementById('quote').scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToPricing = () => {
    document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="permanent-lighting-details" className="py-24 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Permanent Holiday Lights - Better Than Celebright & Gemstone Lights
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Experience professional permanent Christmas light installation in Saskatoon. Our IllumiDEL and Govee permanent outdoor lighting systems offer superior quality to Celebright, Gemstone Lights, and Costco Christmas lights with cutting-edge technology and elegant design.
          </p>
        </div>

        {/* Hero Image/Video Section */}
        <div className="mb-20">
          <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl">
            <img
              src="https://customer-assets.emergentagent.com/job_holiday-lights-pro/artifacts/odn2g9ah_Website_Header_Permanent_Lighting_Saskatoon.webp"
              alt="Permanent Lighting Installation"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
              <h3 className="text-3xl font-bold mb-2">Discreet by Day, Dazzling by Night</h3>
              <p className="text-xl">The most advanced permanent lighting system on the market</p>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="mb-20">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Powerful Features
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-6">
                  <div className="bg-blue-50 w-16 h-16 rounded-lg flex items-center justify-center mb-4">
                    {feature.icon}
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h4>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Use Cases Section */}
        <div className="mb-20">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Endless Possibilities
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <div key={index} className="group relative overflow-hidden rounded-xl shadow-lg">
                <div className="aspect-square">
                  <img
                    src={useCase.image}
                    alt={useCase.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex flex-col justify-end p-6">
                  <h4 className="text-2xl font-bold text-white mb-2">{useCase.title}</h4>
                  <p className="text-gray-200">{useCase.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits List */}
        <div className="mb-20 bg-gray-50 rounded-2xl p-8 md:p-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Why Choose Permanent Lighting?
          </h3>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle2 className="text-blue-500 flex-shrink-0 mt-1" size={24} />
                <p className="text-gray-700 text-lg">{benefit}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Technical Specifications */}
        <div className="mb-20">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Technical Excellence
          </h3>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="border-2 border-blue-100">
              <CardContent className="p-6 text-center">
                <Star className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                <h4 className="text-xl font-bold text-gray-900 mb-2">Premium Quality</h4>
                <p className="text-gray-600">
                  North American-made components with superior LED technology and 90+ CRI for true color reproduction
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-blue-100">
              <CardContent className="p-6 text-center">
                <Shield className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                <h4 className="text-xl font-bold text-gray-900 mb-2">Certified Safe</h4>
                <p className="text-gray-600">
                  Meets UL 2108 and CSA standards. Class 2 system with power-limited supply for maximum safety
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-blue-100">
              <CardContent className="p-6 text-center">
                <CloudSun className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                <h4 className="text-xl font-bold text-gray-900 mb-2">All-Weather</h4>
                <p className="text-gray-600">
                  IP68 rated - highest waterproof rating available. Tested from -40°C to +65°C
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gray-900 rounded-2xl p-8 md:p-12 text-center">
          <h3 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Transform Your Home?
          </h3>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Get a free quote today and discover how permanent lighting can enhance your property's beauty and value.
          </p>
          <Button 
            onClick={scrollToPricing} 
            size="lg" 
            className="bg-blue-500 hover:bg-blue-600 text-white font-bold text-lg px-12 py-6"
          >
            Pricing
          </Button>
        </div>
      </div>
    </section>
  );
};

export default PermanentLightingSection;
